import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Shuffle, RefreshCw } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';

const LEVEL_STYLES = {
    Beginner: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
    Intermediate: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    Advanced: 'bg-red-500/10 text-red-600 border-red-500/30'
};

const VariantCard = ({ variant, selected, onSelect, index }) => (
    <button
        type="button"
        onClick={() => onSelect(variant.id)}
        aria-pressed={selected}
        data-testid={`variant-${variant.id}`}
        style={{ animationDelay: `${index * 40}ms` }}
        className={`animate-fade-in text-left rounded-xl border p-4 transition-[border-color,box-shadow,transform,background-color] duration-200 hover:-translate-y-0.5 hover:shadow-md ${
            selected ? 'border-primary bg-primary/5 ring-2 ring-primary/30' : 'border-border bg-card hover:border-primary/40'
        }`}
    >
        <div className="flex items-start justify-between gap-2">
            <h4 className="text-sm font-semibold leading-snug">{variant.title}</h4>
            {variant.times_practiced > 0 ? (
                <Badge variant="secondary" className="shrink-0 text-[10px]" data-testid={`variant-best-${variant.id}`}>Best {variant.best_score}</Badge>
            ) : (
                <Badge className="shrink-0 border-0 bg-primary/10 text-[10px] text-primary hover:bg-primary/10">New</Badge>
            )}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{variant.persona}</p>
        <p className="mt-2 text-sm line-clamp-2">{variant.summary}</p>
        <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className={`rounded-full border px-2 py-0.5 font-medium ${LEVEL_STYLES[variant.level] || ''}`}>{variant.level}</span>
            <span>{variant.industry}</span>
            {variant.market && <span data-testid={`variant-market-${variant.id}`}>· {variant.market}</span>}
            {variant.times_practiced > 0 && <span>· Practiced {variant.times_practiced}×</span>}
        </div>
    </button>
);

export const ScenarioVariantPicker = ({ api, scenarioType, selectedVariantId, onSelect, onSurprise, loading, onlyVariantId }) => {
    const [variants, setVariants] = useState(null);

    useEffect(() => {
        let active = true;
        setVariants(null);
        api.get(`/simulation/scenario-variants/${scenarioType}`)
            .then(({ data }) => active && setVariants(onlyVariantId ? data.variants.filter((v) => v.id === onlyVariantId) : data.variants))
            .catch(() => {
                if (active) setVariants([]);
                toast.error('Could not load scenarios');
            });
        return () => { active = false; };
    }, [api, scenarioType, onlyVariantId]);

    return (
        <div className="mt-5 space-y-3" data-testid="scenario-variant-picker">
            {onlyVariantId ? (
                <p className="text-sm font-semibold" data-testid="assigned-variant-label">Your assigned scenario</p>
            ) : (
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <p className="text-sm font-semibold">Choose a scenario</p>
                    <p className="text-xs text-muted-foreground">
                        {variants ? `${variants.length} scenarios` : 'Loading scenarios'} · pick one, or let us surprise you with one you haven't tried recently
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={onSurprise} disabled={loading} className="gap-2" data-testid="surprise-me-btn">
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Shuffle className="h-4 w-4" />}
                    Surprise me
                </Button>
            </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {!variants
                    ? [0, 1, 2].map((i) => <Skeleton key={i} className="h-32 rounded-xl" />)
                    : variants.map((v, i) => (
                        <VariantCard key={v.id} variant={v} index={i} selected={selectedVariantId === v.id} onSelect={onSelect} />
                    ))}
            </div>
        </div>
    );
};
